"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import styles from "./LandingChrome.module.css";
import { trackCtaClick } from "@/components/marketing/CtaLink";

const useCases = [
  [
    "founders",
    "Founders & operators",
    "Inbox, calendar, Slack, the week. Run, not just read.",
  ],
  [
    "sales",
    "Sales",
    "Research, follow-ups, demos booked, pipeline kept honest.",
  ],
  [
    "marketing",
    "Marketing",
    "Campaigns, social, email, launches that don’t slip.",
  ],
  [
    "content",
    "Content & creative",
    "Posts, decks, video, images. Made, scheduled, measured.",
  ],
  [
    "support",
    "Customer support",
    "Triage, replies in your tone, escalations, health digests.",
  ],
  [
    "finance",
    "Finance & accounting",
    "Invoices chased, books reconciled, the close memo drafted.",
  ],
  [
    "engineering",
    "Engineering & product",
    "PR review, bug triage, specs, sprint notes, deploys.",
  ],
  [
    "recruiting",
    "Recruiting & people",
    "Screens, panels scheduled, offers sent, onboarding run.",
  ],
  [
    "operations",
    "Operations & admin",
    "Bookings, vendors, travel, the landlord, the downloads folder.",
  ],
  [
    "research",
    "Research & strategy",
    "Market briefs, competitor one-pagers, the board deck.",
  ],
];

export function LandingHeader() {
  const [open, setOpen] = useState(false);
  const [frost, setFrost] = useState(false);
  const [bow, setBow] = useState(false);
  const [dropdownDismissed, setDropdownDismissed] = useState(false);
  const dropdown = useRef<HTMLDivElement>(null);
  const header = useRef<HTMLElement>(null);
  const burger = useRef<HTMLButtonElement>(null);
  const navigation = useRef<HTMLElement>(null);

  useEffect(() => {
    if (open && window.matchMedia("(max-width: 767px)").matches)
      navigation.current?.querySelector<HTMLAnchorElement>("a")?.focus();
  }, [open]);

  useEffect(() => {
    const onScroll = () => {
      setFrost(window.scrollY > 8);
      const footer = document.getElementById("landing-footer");
      setBow(!!footer && footer.getBoundingClientRect().top < 72);
    };
    const onClick = (event: MouseEvent) => {
      if (
        event.target === header.current ||
        !header.current?.contains(event.target as Node)
      ) {
        if (open && header.current?.contains(document.activeElement))
          burger.current?.focus();
        setOpen(false);
      }
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (
        event.key === "Escape" &&
        (open || header.current?.contains(document.activeElement))
      ) {
        setOpen(false);
        // Closing a keyboard-opened dropdown also returns to a visible control.
        if (window.matchMedia("(max-width: 767px)").matches)
          burger.current?.focus();
        else if (dropdown.current?.contains(document.activeElement)) {
          dropdown.current.querySelector<HTMLAnchorElement>("a")?.focus();
          setDropdownDismissed(true);
        }
      }
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    document.addEventListener("click", onClick);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.removeEventListener("click", onClick);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [open]);

  return (
    <header
      ref={header}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node))
          setOpen(false);
      }}
      className={[
        styles.nav,
        frost && styles.frost,
        bow && styles.bow,
        open && styles["menu-open"],
      ]
        .filter(Boolean)
        .join(" ")}
    >
      <a className={styles.wordmark} href="/" aria-label="Vestra home">
        <Image
          className={styles.mark}
          src="/landing-d6/vestra-mark.svg"
          alt=""
          width={26}
          height={26}
        />
        <span className={styles["wm-text"]}>Vestra</span>
      </a>
      <nav
        ref={navigation}
        id="landing-primary-navigation"
        className={styles["nav-links"]}
        aria-label="Primary"
        onClick={(event) => {
          if ((event.target as HTMLElement).closest("a")) setOpen(false);
        }}
      >
        <a href="/#bash">Bash</a>
        <a href="/#nami">Nami</a>
        <a href="/#abilities">Abilities</a>
        <div
          ref={dropdown}
          className={[
            styles["nav-dd"],
            dropdownDismissed && styles["dd-dismissed"],
          ]
            .filter(Boolean)
            .join(" ")}
          onMouseEnter={() => setDropdownDismissed(false)}
          onFocus={(event) => {
            if (!event.currentTarget.contains(event.relatedTarget as Node))
              setDropdownDismissed(false);
          }}
        >
          <Link href="/use-cases">
            Use cases{" "}
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </Link>
          <div className={styles["dd-panel"]} aria-label="Use cases">
            <div className={styles["dd-k"]}>Built for</div>
            <div className={styles["dd-grid"]}>
              {useCases.map(([slug, title, description]) => (
                <a key={slug} href={`/use-cases/${slug}`}>
                  <b>{title}</b>
                  <span>{description}</span>
                </a>
              ))}
            </div>
            <Link className={styles["dd-all"]} href="/use-cases">
              All use cases → <small>10 rooms in the office</small>
            </Link>
          </div>
        </div>
        <a href="/pricing">Pricing</a>
        <a
          className={styles["m-only"]}
          href="/login"
          onClick={() => trackCtaClick("nav_mobile", "vs")}
        >
          Get started
        </a>
      </nav>
      <div className={styles["nav-end"]}>
        <a
          className={[
            styles.btn,
            styles["btn-ghost"],
            styles["btn-nav"],
            styles["nav-book"],
          ].join(" ")}
          href="/login"
          onClick={() => trackCtaClick("nav", "vs")}
        >
          Get started
        </a>
        <button
          ref={burger}
          className={styles["nav-burger"]}
          type="button"
          aria-label={open ? "Close menu" : "Open menu"}
          aria-expanded={open}
          aria-controls="landing-primary-navigation"
          onClick={() => setOpen(!open)}
        >
          <span />
          <span />
          <span />
        </button>
      </div>
    </header>
  );
}

const footerColumns = [
  {
    title: "Product",
    links: [
      ["Start on the web", "/login"],
      ["Pricing", "/pricing"],
      ["Abilities", "/abilities"],
      ["Use cases", "/use-cases"],
    ],
  },
  {
    title: "The tour",
    links: [
      ["Omnipotent", "/#bash"],
      ["Omnipresent", "/#summon-jump"],
      ["Omniscient", "/#nami"],
      ["The loop", "/#loop"],
    ],
  },
  {
    title: "Learn",
    links: [
      ["The handbook", "/handbook"],
      ["Compare Vestra", "/vs"],
      ["Book a call", "https://calendly.com/keshav-vestra/vestra-onboarding"],
    ],
  },
  {
    title: "Legal",
    links: [
      ["Privacy policy", "/policies/privacy"],
      ["Terms of service", "/policies/terms"],
    ],
  },
];

export function LandingFooter() {
  return (
    <footer id="landing-footer" className={styles.footer}>
      <div className={styles["ft-sky"]} aria-hidden="true">
        <Image
          src="/landing-d6/img/footer-sky-gold.webp"
          alt=""
          fill
          sizes="100vw"
        />
      </div>
      <div className={styles["ft-grass"]} aria-hidden="true">
        <Image
          src="/landing-d6/img/footer-grass-wheat.webp"
          alt=""
          fill
          sizes="100vw"
        />
      </div>
      <div className={styles["ft-top"]}>
        <div className={styles["ft-brand"]}>
          <Image
            src="/landing-d6/vestra-mark.svg"
            alt="Vestra"
            width={34}
            height={34}
          />
          <span>Private beta, 2026</span>
        </div>
        <nav className={styles["ft-cols"]} aria-label="Footer">
          {footerColumns.map(({ title, links }) => (
            <div key={title} className={styles["ft-col"]}>
              <b>{title}</b>
              {links.map(([label, href]) => (
                <a
                  key={href}
                  href={href}
                  {...(href.startsWith("https:")
                    ? { target: "_blank", rel: "noopener noreferrer" }
                    : {})}
                  {...(href === "/login"
                    ? { onClick: () => trackCtaClick("footer", "vs") }
                    : {})}
                >
                  {label}
                </a>
              ))}
            </div>
          ))}
        </nav>
      </div>
      <div className={styles["ft-mark"]} aria-hidden="true">
        <span className={styles["ft-word"]}>Vestra</span>
      </div>
      <div className={styles["ft-made"]}>
        Built with{" "}
        <span role="img" aria-label="love">
          ❤️
        </span>{" "}
        from India{" "}
        <span role="img" aria-label="India">
          🇮🇳
        </span>
      </div>
    </footer>
  );
}
